// CheckoutForm.js
import React from 'react';
import { inject, observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import _ from 'lodash';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import actions from 'api/actions';
import emailRegex from 'utils/emailRegex';
import CardSection from './card-section';
import PoweredByStripe from 'assets/img/powered_by_stripe.png';
import styles from './styles.module.scss';

const validations = {
  firstName: {
    validate: field => Boolean(field) && field.length > 0,
    message: 'First name is invalid',
  },
  lastName: {
    validate: field => Boolean(field) && field.length > 0,
    message: 'Last Name is invalid',
  },
  email: {
    validate: field => field.length > 3 && emailRegex.test(field),
    message: 'Email is invalid',
  },
  password: {
    validate: field => field.length > 6,
    message: 'Password is too short',
  },
};

class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);

    const { parent } = this.props;

    this.state = {
      email: parent && parent.email ? parent.email : '',
      firstName: parent && parent.first_name ? parent.first_name : '',
      lastName: parent && parent.last_name ? parent.last_name : '',
      password: '',
      errors: {},
    };

    this.onPlanUserChange = this.onPlanUserChange.bind(this);
  }

  handleSubmit = ev => {
    ev.preventDefault();

    this.props.spinnerStore.on();

    const { email, firstName, lastName, password } = this.state;

    const { parent } = this.props;

    if (!parent) {
      const errors = ['firstName', 'lastName', 'email', 'password'].reduce(
        (errors, field) => {
          if (!validations[field].validate(this.state[field])) {
            errors[field] = validations[field].message;
          }

          return errors;
        },
        {},
      );

      if (Object.keys(errors).length > 0) {
        this.setState({ errors });
        this.props.spinnerStore.off();
        return;
      }
    }

    const {
      elements,
      stripe,
      planId,
      onComplete,
      disablePaymentElements,
      appliedDiscount,
      priceInCents,
    } = this.props;

    if (disablePaymentElements) {
      onComplete({
        email,
        token_id: null,
        first_name: firstName,
        last_name: lastName,
        plan_id: planId,
        discount_code: appliedDiscount.code,
        password,
      });
    } else {
      elements.submit();
      actions
        .rawPost('stripe/payment-intent', { amount: priceInCents })
        .then(response => {
          const { client_secret: clientSecret } = response.data;
          return stripe.confirmPayment({
            elements,
            clientSecret,
            redirect: 'if_required',
            confirmParams: {
              return_url:
                window.location.protocol +
                '//' +
                window.location.hostname +
                ':' +
                window.location.port +
                '/login',
            },
          });
        })
        .then(response => {
          if (response.error) {
            this.handleConfirmPaymentError(response.error);
          } else {
            console.log('response from stripe', response);
            return onComplete({
              email,
              payment_intent: response.paymentIntent.id,
              first_name: firstName,
              last_name: lastName,
              plan_id: planId,
              discount_code: appliedDiscount && appliedDiscount.code,
              password,
            });
          }
        })
        .catch(e => {
          console.log(e);
          Sentry.captureException(e);
        });
    }
  };

  handleConfirmPaymentError(error) {
    swal('Payment Details', 'Please review your payment details', 'error');
    console.error(error);
    Sentry.captureException(error);
  }

  onPlanUserChange(e) {
    const { errors } = this.state;

    if (
      errors[e.target.name] &&
      validations[e.target.name] &&
      validations[e.target.name].validate(e.target.value)
    ) {
      const newErrorObject = _.omit(errors, e.target.name);
      this.setState({
        errors: newErrorObject,
        [e.target.name]: e.target.value,
      });
    } else if (
      !errors[e.target.name] &&
      validations[e.target.name] &&
      !validations[e.target.name].validate(e.target.value)
    ) {
      const errors = _.merge(
        {},
        { [e.target.name]: validations[e.target.name].message },
      );
      this.setState({ errors, [e.target.name]: e.target.value });
    } else {
      this.setState({ [e.target.name]: e.target.value });
    }
  }

  render() {
    const { email, firstName, lastName, password, errors } = this.state;

    const {
      parent,
      disablePaymentElements,
      spinnerStore,
      modalDisabled,
      stripe,
      elements,
    } = this.props;
    return (
      <form id="purchase" onSubmit={this.handleSubmit}>
        <div className={styles.billingSection}>
          <h5 className="font-heavy">Your Info</h5>
          <div className="form-row">
            <div className="col-6">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  className="form-control"
                  type="text"
                  name="firstName"
                  value={firstName}
                  disabled={parent && parent.first_name}
                  onChange={this.onPlanUserChange}
                />
                {errors.firstName && (
                  <p className="text-red font-heavy mt-1">{errors.firstName}</p>
                )}
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  className="form-control"
                  type="text"
                  name="lastName"
                  value={lastName}
                  disabled={parent && parent.last_name}
                  onChange={this.onPlanUserChange}
                />
                {errors.lastName && (
                  <p className="text-red font-heavy mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              className="form-control"
              type="text"
              name="email"
              value={email}
              disabled={parent && parent.email}
              onChange={this.onPlanUserChange}
            />
            {!parent && (
              <small className="form-text text-gray">
                This email address will be used for your parent account.
              </small>
            )}
            {errors.email && (
              <p className="text-red font-heavy mt-1">{errors.email}</p>
            )}
          </div>
          {(!parent || (parent && !parent.email)) && (
            <div className="form-group">
              <label htmlFor="email">Password</label>
              <input
                className="form-control"
                type="password"
                name="password"
                value={password}
                disabled={parent && parent.email}
                onChange={this.onPlanUserChange}
              />
              <small className="form-text text-gray">
                This password will be used for your parent account.
              </small>
              {errors.password && (
                <p className="text-red font-heavy mt-1">{errors.password}</p>
              )}
            </div>
          )}
        </div>
        {!disablePaymentElements && (
          <React.Fragment>
            <CardSection stripe={stripe} elements={elements} />
            <div className="mt-3 d-flex flex-wrap justify-content-end align-items-center text-cloudburst">
              <FontAwesomeIcon
                fixedWidth
                icon="lock"
                size="2x"
                className="mr-1"
              />
              <a href="https://stripe.com/" target="_blank" rel="noreferrer">
                <img src={PoweredByStripe} alt="Powered by Stripe" />
              </a>
              <FontAwesomeIcon
                fixedWidth
                icon={['fab', 'cc-visa']}
                size="3x"
                className="ml-1 mr-1"
              />
              <FontAwesomeIcon
                fixedWidth
                icon={['fab', 'cc-mastercard']}
                size="3x"
                className="mr-1"
              />
              <FontAwesomeIcon
                fixedWidth
                icon={['fab', 'cc-discover']}
                size="3x"
                className="mr-1"
              />
            </div>
          </React.Fragment>
        )}
        <button
          disabled={spinnerStore.active}
          className={`${
            spinnerStore.active ? 'disabled' : ''
          } blue d-block w-100 mx-auto my-3 font-heavy font-lg`}
          type="submit"
        >
          Confirm order
        </button>
      </form>
    );
  }
}

const ConnectedCheckoutForm = inject('spinnerStore')(observer(CheckoutForm));

export default ConnectedCheckoutForm;
